import { useMemo, useState } from 'react';

//* MUI
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// * Redux
import { showSnackbar } from '@/redux/snackbarSlice';
import { useDispatch } from 'react-redux';

// * Utils
import { mapUnitsToDataStreams } from '../../utils/energyTreeFunctions';
import { getGroupedOptions } from '../../utils/nodeTypeMapper';

// * API
import { useGetUnitsByAccountQuery } from '@/api/recalc/endpoints/unitsApiSlice';
import { usePostEnergyTreeNodesMutation } from '@/api/recalcV2/endpoints/energyTreeNodesApiSlice';

const CreateRootNodeDialog = ({ open, onClose, energyGroup }) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const groupedOptions = getGroupedOptions();

  const [formState, setFormState] = useState({
    name: '',
    type: 'Hovedmåler',
    energyGroupId: energyGroup?.energyGroupId,
    parentId: null,
    dataStreams: [],
  });

  const { data: units } = useGetUnitsByAccountQuery(energyGroup?.accountId, {
    skip: !energyGroup?.accountId,
  });
  const [postEnergyTreeNodes] = usePostEnergyTreeNodesMutation();

  const handleClose = () => {
    setFormState({
      name: '',
      type: '',
      dataStreams: [],
      energyGroupId: energyGroup?.energyGroupId || null,
    });
    onClose();
  };

  const handleAddNode = async () => {
    try {
      const createdNode = await postEnergyTreeNodes({
        energyTreeNode: formState,
        accountId: energyGroup.accountId,
      }).unwrap();
      dispatch(showSnackbar({ message: 'Node tilføjet', severity: 'success' }));
      handleClose();
      return createdNode;
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Der opstod en fejl i tilføjelse af node',
          severity: 'error',
        }),
      );
      return null; // Optional: return null on error
    }
  };

  const decoratedUnits = useMemo(() => {
    const supportedUnitTypeIds = [28, 30, 61];
    return units?.map((unit) => {
      const isSupported = supportedUnitTypeIds.includes(
        unit.UnitType?.UnitTypeId,
      );
      const notSupportedReason = !isSupported ? 'Understøttes ikke' : null;

      return {
        ...unit,
        notSupportedReason,
        isDisabled: Boolean(notSupportedReason), // optional unified flag
      };
    });
  }, [units]);

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={handleClose}
      PaperProps={{
        elevation: 0,
        style: {
          backgroundColor: theme.palette.mode === 'dark' ? '#000' : '#fff',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '8px',
        },
      }}
    >
      <DialogTitle>Tilføj første node</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          For at oprette en ny node, skal du vælge de Units, som skal associeres
          med denne node. Dette vil typisk være en hovedmåler som repræsentere
          det overordnede forbrug.
        </Typography>
        <Autocomplete
          multiple
          options={decoratedUnits}
          getOptionLabel={(unit) => `(${unit.UnitId}) ${unit.Name}`}
          value={formState.dataStreams.map((ds) => {
            return decoratedUnits.find((unit) => unit.UnitId === ds.unitId);
          })}
          onChange={(e, newValue) => {
            setFormState({
              ...formState,
              dataStreams: mapUnitsToDataStreams(newValue),
            });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Units"
              helperText="Vælg de Units som skal associeres med denne node. Dette vil typisk være en hovedmåler eller en tavle."
              placeholder="Vælg Units"
              variant="filled"
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiFilledInput-root': {
                  borderRadius: 1,
                  '&:before': { borderBottom: 'none' },
                  '&:after': { borderBottom: 'none' },
                  '&:hover:before': { borderBottom: 'none' },
                },
                my: 1,
              }}
            />
          )}
        />

        <TextField
          select
          label="Node Type"
          helperText="Det er kun muligt at oprette en hovedmåler som første node."
          value={formState.type}
          onChange={(e) => setFormState({ ...formState, type: e.target.value })}
          SelectProps={{ native: true }}
          variant="filled"
          fullWidth
          InputLabelProps={{ shrink: true }}
          InputProps={{ disableUnderline: true }}
          sx={{
            '& .MuiFilledInput-root': {
              borderRadius: 1,
              overflow: 'hidden',
            },
            my: 1,
          }}
        >
          <option value="" disabled>
            Vælg type
          </option>
          {Object.entries(groupedOptions).map(([groupLabel, options]) => (
            <optgroup key={groupLabel} label={groupLabel}>
              {options.map(({ value, label }) => (
                <option
                  key={value}
                  value={value}
                  disabled={value !== 'Hovedmåler'}
                >
                  {label}
                </option>
              ))}
            </optgroup>
          ))}
        </TextField>

        <TextField
          label="Navn"
          value={formState.name}
          helperText="Indtast navnet på den nye node."
          onChange={(e) => setFormState({ ...formState, name: e.target.value })}
          fullWidth
          variant="filled"
          InputProps={{ disableUnderline: true }}
          sx={{
            '& .MuiFilledInput-root': {
              borderRadius: 1,
              overflow: 'hidden',
            },
            my: 1,
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button variant={'text'} onClick={handleClose}>
          Afbryd
        </Button>
        <Button variant={'contained'} onClick={handleAddNode}>
          Tilføj
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateRootNodeDialog;
